import { MapContainer, TileLayer, Marker, Polygon, Polyline, CircleMarker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { useEffect, useMemo } from "react";
import { STATUS_CONFIG } from "@/lib/constants";

delete L.Icon.Default.prototype._getIconUrl;

const buildDivIcon = (asset, isSelected) => {
  const config = STATUS_CONFIG[asset.status] || STATUS_CONFIG.idle;
  const size = isSelected ? 30 : 22;
  const pulseClass = asset.status === "alert" ? "ll-marker-pulse-alert" : "ll-marker-pulse";
  const showPulse = ["active", "transit", "alert"].includes(asset.status);

  return L.divIcon({
    className: "ll-leaflet-icon",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `
      <div class="ll-marker-wrapper" style="width:${size}px;height:${size}px;">
        ${showPulse ? `<div class="${pulseClass}" style="background:${config.color};"></div>` : ""}
        <div class="ll-marker-dot" style="background:${config.color}; ${isSelected ? "outline: 2px solid #fff; outline-offset: 2px;" : ""}">
          ${asset.type?.[0]?.toUpperCase() || "?"}
        </div>
      </div>
    `,
  });
};

function MapAutoFit({ assets, runKey }) {
  const map = useMap();
  useEffect(() => {
    if (!assets || assets.length === 0) return;
    const bounds = L.latLngBounds(assets.map((a) => [a.lat, a.lng]));
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 13 });
  }, [runKey]); // eslint-disable-line
  return null;
}

function DrawHandler({ drawing, onAddPoint, onFinish }) {
  useMapEvents({
    click(e) {
      if (!drawing) return;
      onAddPoint([e.latlng.lat, e.latlng.lng]);
    },
    dblclick(e) {
      if (!drawing) return;
      e.originalEvent.preventDefault();
      onFinish();
    },
  });
  return null;
}

export default function FleetMap({
  assets,
  selectedId,
  onSelect,
  zones = [],
  drawing = false,
  pendingPolygon = [],
  onAddDrawPoint,
  onFinishDraw,
  trail = null, // { points: [{lat,lng,...}] }
}) {
  const center = useMemo(() => {
    if (assets && assets.length > 0) return [assets[0].lat, assets[0].lng];
    return [47.6062, -122.3321];
  }, [assets]);

  // Only auto-fit once on initial mount, not when zones/drawing changes
  const fitKey = useMemo(() => "initial", []);

  return (
    <MapContainer
      center={center}
      zoom={12}
      style={{ height: "100%", width: "100%", background: "#111", cursor: drawing ? "crosshair" : "" }}
      zoomControl={true}
      attributionControl={true}
      doubleClickZoom={!drawing}
      data-testid="fleet-map"
    >
      <TileLayer
        attribution='&copy; OpenStreetMap'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapAutoFit assets={assets} runKey={fitKey} />
      <DrawHandler
        drawing={drawing}
        onAddPoint={onAddDrawPoint}
        onFinish={onFinishDraw}
      />

      {/* Saved zones */}
      {zones.map((z) => (
        <Polygon
          key={z.id}
          positions={z.polygon}
          pathOptions={{
            color: z.color,
            fillColor: z.color,
            fillOpacity: 0.12,
            weight: 2,
            dashArray: z.kind === "restricted" ? "8,6" : null,
          }}
        />
      ))}

      {/* Pending draw polygon */}
      {pendingPolygon && pendingPolygon.length > 0 && (
        <>
          <Polyline
            positions={pendingPolygon}
            pathOptions={{ color: "#ff8c00", weight: 3, dashArray: "6,6" }}
          />
          {pendingPolygon.length >= 3 && (
            <Polygon
              positions={pendingPolygon}
              pathOptions={{ color: "#ff8c00", fillColor: "#ff8c00", fillOpacity: 0.15, weight: 0 }}
            />
          )}
          {pendingPolygon.map((p, i) => (
            <CircleMarker
              key={i}
              center={p}
              radius={5}
              pathOptions={{ color: "#fff", fillColor: "#ff8c00", fillOpacity: 1, weight: 2 }}
            />
          ))}
        </>
      )}

      {/* Asset trail */}
      {trail && trail.points && trail.points.length > 1 && (
        <Polyline
          positions={trail.points.map((p) => [p.lat, p.lng])}
          pathOptions={{ color: "#4682b4", weight: 3, opacity: 0.7 }}
        />
      )}

      {/* Asset markers */}
      {assets.map((asset) => (
        <Marker
          key={asset.id}
          position={[asset.lat, asset.lng]}
          icon={buildDivIcon(asset, asset.id === selectedId)}
          eventHandlers={{ click: () => onSelect && onSelect(asset) }}
        />
      ))}
    </MapContainer>
  );
}
